import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, FileSpreadsheet, Download, Trash2, Users, Settings2 } from 'lucide-react';
import { OfficeEditButton } from '../../components/office/OfficeEditButton';
import { OfficeVersionsButton } from '../../components/office/OfficeVersionsDialog';

const getFileIcon = (type) => (type === 'docx' ? FileText : FileSpreadsheet);

export const TemplateCard = ({ template, onGenerate, onGenerateList, onOpenInWord, onEdit, onDelete, onRefresh }) => {
  const FileIcon = getFileIcon(template.file_type);
  return (
    <Card className="border-border/60 hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${template.file_type === 'docx' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
              <FileIcon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">{template.name}</CardTitle>
              <Badge variant="secondary" className="text-xs mt-1">
                {template.category}
              </Badge>
            </div>
          </div>
          <Badge variant="outline" className="text-xs uppercase">
            {template.file_type}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {template.description && (
          <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
        )}

        {template.placeholders?.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-muted-foreground mb-1">
              {template.placeholders.length} Platzhalter erkannt
            </p>
            <div className="flex flex-wrap gap-1">
              {template.placeholders.slice(0, 3).map((p) => (
                <Badge key={p} variant="outline" className="text-xs font-mono">
                  {p}
                </Badge>
              ))}
              {template.placeholders.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{template.placeholders.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground mb-4">
          Erstellt von {template.created_by}
          {template.version > 1 && (
            <span className="ml-1">· v{template.version}{template.last_edited_by ? ` · zuletzt: ${template.last_edited_by}` : ''}</span>
          )}
        </p>

        <div className="flex gap-2 flex-wrap">
          <Button
            variant="default"
            size="sm"
            className="flex-1 min-w-[110px] rounded-full"
            onClick={() => onGenerate(template)}
            data-testid={`generate-template-${template.id}`}
          >
            <Download className="h-4 w-4 mr-1" />
            Pro MA
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 min-w-[110px] rounded-full"
            onClick={() => onGenerateList?.(template)}
            data-testid={`generate-list-${template.id}`}
            title="Eine Liste mit allen Mitarbeitern eines Bereichs erzeugen"
          >
            <Users className="h-4 w-4 mr-1" />
            Liste
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => onEdit(template)}
            title="Bearbeiten (Stammdaten & neue Version)"
            data-testid={`edit-template-${template.id}`}
          >
            <Settings2 className="h-4 w-4" />
          </Button>
          <OfficeEditButton
            source="template" id={template.id} fileType={template.file_type}
            onReturn={onRefresh} label={template.file_type === 'xlsx' ? 'In Excel' : 'In Word'}
            testId={`office-edit-template-${template.id}`}
          />
          <OfficeVersionsButton source="template" id={template.id} fileType={template.file_type} onChanged={onRefresh} />
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => onOpenInWord(template)}
            title="Herunterladen"
            data-testid={`download-template-${template.id}`}
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full text-destructive hover:text-destructive"
            onClick={() => onDelete(template.id)}
            data-testid={`delete-template-${template.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
